import './quiz-embed-component';

import {
  createEmbedBlockSchema,
  defineEmbedModel,
  type EmbedHtmlBlockProps,
} from '@blocksuite/affine-model';
import { BlockModel } from '@blocksuite/store';
import { literal } from 'lit/static-html.js';

import { PiqueQuizEmbedBlockService } from './quiz-embed-service';

// Define quiz question type
export interface QuizQuestion {
  id: string;
  question: string;
  options: string[];
  answer: string;
  explanation: string;
  imageKeywords?: string;
  imageUrl?: string;
}

// Define quiz template type
export interface QuizTemplate {
  title: string;
  questions: QuizQuestion[];
}

// Define the block props
export interface PiqueQuizEmbedBlockProps extends EmbedHtmlBlockProps {
  quiz: string; // JSON string of quiz template
  currentQuestionIndex: number;
  userAnswers: Record<string, string>; // Map of question ID to selected answer
  score?: number;
  isComplete: boolean;
}

// Create default props
const defaultQuizEmbedProps: PiqueQuizEmbedBlockProps = {
  style: 'html', // Reuse html style for now
  quiz: '',
  currentQuestionIndex: 0,
  userAnswers: {},
  score: undefined,
  isComplete: false,
  html: '', // Required by EmbedHtmlBlockProps
  caption: null,
};

// Create the model with methods for quiz interaction
export class PiqueQuizEmbedModel extends defineEmbedModel<PiqueQuizEmbedBlockProps>(
  BlockModel
) {
  // Get the parsed quiz template
  getQuiz(): QuizTemplate | null {
    try {
      return JSON.parse(this.quiz);
    } catch {
      return null;
    }
  }

  // Get current question
  getCurrentQuestion(): QuizQuestion | null {
    const quiz = this.getQuiz();
    if (!quiz || !quiz.questions.length) return null;
    return quiz.questions[this.currentQuestionIndex] ?? null;
  }

  // Answer current question
  answerQuestion(questionId: string, answer: string) {
    this.userAnswers = {
      ...this.userAnswers,
      [questionId]: answer,
    };

    // Update score in real-time
    const quiz = this.getQuiz();
    if (!quiz) return;

    let correct = 0;
    quiz.questions.forEach(question => {
      if (this.userAnswers[question.id] === question.answer) {
        correct++;
      }
    });

    this.score = (correct / quiz.questions.length) * 100;
  }

  // Move to next question
  nextQuestion() {
    const quiz = this.getQuiz();
    if (!quiz) return;

    if (this.currentQuestionIndex < quiz.questions.length - 1) {
      this.currentQuestionIndex++;
    } else {
      this.completeQuiz();
    }
  }

  // Move to previous question
  previousQuestion() {
    if (this.currentQuestionIndex > 0) {
      this.currentQuestionIndex--;
    }
  }

  // Complete the quiz and calculate score
  completeQuiz() {
    const quiz = this.getQuiz();
    if (!quiz) return;

    let correct = 0;
    quiz.questions.forEach(question => {
      if (this.userAnswers[question.id] === question.answer) {
        correct++;
      }
    });

    this.score = (correct / quiz.questions.length) * 100;
    this.isComplete = true;
  }

  // Reset the quiz
  resetQuiz() {
    this.currentQuestionIndex = 0;
    this.userAnswers = {};
    this.score = undefined;
    this.isComplete = false;
  }
}

// Create the schema
export const PiqueQuizEmbedBlockSchema = createEmbedBlockSchema({
  name: 'pique-quiz',
  version: 1,
  toModel: () => new PiqueQuizEmbedModel(),
  props: (): PiqueQuizEmbedBlockProps => defaultQuizEmbedProps,
});

// Define the block spec
export const PiqueQuizEmbedBlockSpec = {
  schema: PiqueQuizEmbedBlockSchema,
  service: PiqueQuizEmbedBlockService,
  view: {
    component: literal`pique-quiz-embed-block`,
  },
};
